import { useCallback, useRef } from 'react';
import { useAtomValue, useUpdateAtom } from 'jotai/utils';
import {
  chatMessagesAtom,
  chatStreamingAtom,
  chatSessionIdAtom,
  chatTurnIdAtom,
  stripQueryCriteriaWildcards,
} from 'src/shared/chat';
import { applyAiSearchIntentAtom } from 'src/shared/dashboardFilters';
import { effectiveChatActionModeAtom } from 'src/shared/chatPreferences';
import { useUIActionDispatcher } from './useUIActionDispatcher';
import { createSSEParser } from './sseParser';
import type {
  TextDeltaEvent,
  ToolStartEvent,
  ToolResultEvent,
  MessageDoneEvent,
  ErrorEvent,
  ChatMessage,
  ActionCardData,
  ChatPlan,
  PlanProposedEvent,
  PlanStatusEvent,
  UIAction,
} from './types';

// propose_plan is the proxy-local plan tool; the plan renders as a
// PlanCard (plan_proposed), so it gets no step card of its own.
const PLAN_TOOL = 'propose_plan';

// Tool results that report a failure render as error cards rather than
// "Completed".
export function isErrorResult(result: Record<string, any> | undefined): boolean {
  if (!result) return false;
  return result.is_error === true || result.isError === true || result.error === true || typeof result.error === 'string';
}

// A call the proxy's plan gate refused (`blocked: true`, e.g. plan_required).
// It never ran, and the agent normally proposes a plan or retries straight
// away, so its card is dropped rather than shown as Failed (mcp-server#112).
// Any ui_action on it is ignored too: older proxies turned the refusal's
// internal reason into a toast.
export function isBlockedResult(result: Record<string, any> | undefined): boolean {
  return result?.blocked === true;
}

export function useSSEStream() {
  const setMessages = useUpdateAtom(chatMessagesAtom);
  const setStreaming = useUpdateAtom(chatStreamingAtom);
  const setSessionId = useUpdateAtom(chatSessionIdAtom);
  const setTurnId = useUpdateAtom(chatTurnIdAtom);
  const applyAiSearchIntent = useUpdateAtom(applyAiSearchIntentAtom);
  const actionMode = useAtomValue(effectiveChatActionModeAtom);
  const dispatchUIAction = useUIActionDispatcher();

  // Defensive paragraph-break tracker. Set true whenever a tool_start
  // or tool_result event lands; consumed by the next text_delta. The
  // proxy now emits its own "\n\n" text_delta between agentic rounds
  // (see RunAgenticLoop in filespin-mcp-server claude.go), so the
  // regex guard below prevents double-spacing on the happy path. This
  // ref also covers older proxies and replayed sessions where the
  // server-side separator is absent.
  const sawToolEventSinceTextRef = useRef(false);

  const processStream = useCallback(
    async (response: Response) => {
      setStreaming(true);
      const reader = response.body!.getReader();

      const msgId = `msg-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      const assistantMsg: ChatMessage = {
        id: msgId,
        role: 'assistant',
        content: '',
        actions: [],
        timestamp: Date.now(),
      };
      setMessages((prev) => [...prev, assistantMsg]);
      sawToolEventSinceTextRef.current = false;

      const toolInputs = new Map<string, Record<string, any>>();

      // markStopped resolves a user-cancelled turn on the assistant
      // message: it flips any still-spinning tool cards ('pending' →
      // 'cancelled') and appends a subtle "(stopped)" marker to the
      // text. Idempotent so it can run from both the server-sent
      // `message_cancelled` event AND the local fetch-abort path
      // (whichever wins the race) without double-marking.
      const STOPPED_MARKER = '_(stopped)_';
      const markStopped = () => {
        setMessages((prev) =>
          prev.map((m) => {
            if (m.id !== msgId) return m;
            const actions = (m.actions || []).map((a) =>
              a.status === 'pending' ? { ...a, status: 'cancelled' as const } : a,
            );
            const content = m.content.endsWith(STOPPED_MARKER)
              ? m.content
              : m.content + (m.content ? '\n\n' : '') + STOPPED_MARKER;
            return { ...m, content, actions };
          }),
        );
      };

      const handleEvent = (type: string, data: any) => {
        switch (type) {
          case 'turn_id': {
            setTurnId(data.turn_id);
            break;
          }
          case 'session_id': {
            setSessionId(data.session_id);
            break;
          }
          case 'text_delta': {
            const { delta } = data as TextDeltaEvent;
            const needsSeparator = sawToolEventSinceTextRef.current;
            sawToolEventSinceTextRef.current = false;
            setMessages((prev) =>
              prev.map((m) => {
                if (m.id !== msgId) return m;
                const sep =
                  needsSeparator && m.content.length > 0 && !/\n\n$/.test(m.content) && !/^\n\n/.test(delta)
                    ? '\n\n'
                    : '';
                return { ...m, content: m.content + sep + delta };
              }),
            );
            break;
          }
          case 'tool_start': {
            const { tool_use_id, tool, input } = data as ToolStartEvent;
            if (tool === PLAN_TOOL) {
              sawToolEventSinceTextRef.current = true;
              break;
            }
            toolInputs.set(tool, input || {});
            const card: ActionCardData = {
              id: `action-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
              toolUseId: tool_use_id,
              tool,
              input: input || {},
              status: 'pending',
            };
            sawToolEventSinceTextRef.current = true;
            setMessages((prev) =>
              prev.map((m) =>
                m.id === msgId ? { ...m, actions: [...(m.actions || []), card] } : m,
              ),
            );
            break;
          }
          case 'tool_result': {
            const { tool_use_id, tool, result, ui_action } = data as ToolResultEvent;
            if (tool === PLAN_TOOL) {
              sawToolEventSinceTextRef.current = true;
              break;
            }
            const blocked = isBlockedResult(result);
            const failed = isErrorResult(result);
            const queryCriteria = ui_action?.query_criteria
              ?? result?.query_criteria as Record<string, any> | undefined;
            const rollbackOpId =
              !failed &&
              (tool === 'update_metadata' || tool === 'tag_assets_bulk') &&
              result?.can_rollback === true &&
              typeof result?.rollback_op_id === 'string' &&
              result.rollback_op_id.length > 0
                ? (result.rollback_op_id as string)
                : undefined;
            sawToolEventSinceTextRef.current = true;
            setMessages((prev) =>
              prev.map((m) => {
                if (m.id !== msgId) return m;
                const actions = (m.actions || []).slice();
                // Match by tool_use_id when the proxy provides it (real
                // Claude turns and updated mocks). Fall back to the
                // first pending action of this tool name so we keep
                // working against older proxies, replay paths, and
                // legacy mocks. Critically, we update exactly ONE
                // action per result — the previous Array.map would
                // flip every pending sibling at once and silently
                // drop later results.
                let idx = -1;
                if (tool_use_id) {
                  idx = actions.findIndex((a) => a.toolUseId === tool_use_id);
                }
                if (idx === -1) {
                  idx = actions.findIndex(
                    (a) => a.tool === tool && a.status === 'pending',
                  );
                }
                if (idx === -1) return m;
                if (blocked) {
                  actions.splice(idx, 1);
                  return { ...m, actions };
                }
                actions[idx] = {
                  ...actions[idx],
                  status: failed ? 'error' : 'success',
                  result: result || {},
                  uiAction: ui_action,
                  queryCriteria,
                  ...(rollbackOpId ? { rollback: { opId: rollbackOpId } } : {}),
                };
                return { ...m, actions };
              }),
            );

            if (blocked) break;

            if (tool === 'search_assets') {
              const rawQc = queryCriteria || toolInputs.get('search_assets') || {};
              const qc = stripQueryCriteriaWildcards(rawQc);

              if (actionMode === 'navigate') {
                applyAiSearchIntent({
                  keyword: qc.query || qc.keyword,
                  content_type: qc.content_type,
                  file_name: qc.file_name,
                  status: qc.status,
                  upload_time_range: qc.upload_time_range,
                  sort_by: qc.sort_by,
                  schema_criteria: qc.schema_criteria,
                });
              }
            } else if (ui_action?.search_asset_ids?.length && actionMode === 'navigate') {
              applyAiSearchIntent({
                keyword: ui_action.search_asset_ids.join(' '),
              });
            }

            if (ui_action) {
              dispatchUIAction(ui_action);
            }
            break;
          }
          case 'plan_proposed': {
            const ev = data as PlanProposedEvent;
            const plan: ChatPlan = {
              id: ev.plan_id,
              summary: ev.summary,
              steps: ev.steps || [],
              assetCount: ev.asset_count,
              matchedCount: ev.matched_count,
              status: ev.status === 'approved' ? 'approved' : 'pending',
              autoApproved: ev.auto_approved,
              approvalReason: ev.approval_reason,
              assetLimit: ev.status === 'approved' ? ev.asset_count : undefined,
            };
            // Only the newest plan can be answered; the proxy keeps one
            // plan per session, so older unanswered cards are dead.
            setMessages((prev) =>
              prev.map((m) => {
                if (m.id === msgId) return { ...m, plan };
                if (m.plan?.status === 'pending') {
                  return { ...m, plan: { ...m.plan, status: 'replaced' } };
                }
                return m;
              }),
            );
            break;
          }
          case 'plan_status': {
            const { plan_id, status, asset_limit } = data as PlanStatusEvent;
            // The card lives on an earlier assistant message.
            setMessages((prev) =>
              prev.map((m) =>
                m.plan?.id === plan_id
                  ? { ...m, plan: { ...m.plan, status, assetLimit: asset_limit ?? m.plan.assetLimit } }
                  : m,
              ),
            );
            break;
          }
          case 'message_done': {
            const { usage } = data as MessageDoneEvent;
            setMessages((prev) =>
              prev.map((m) => (m.id === msgId ? { ...m, usage } : m)),
            );
            break;
          }
          case 'message_cancelled': {
            // User hit Stop and the stream stayed open long enough to
            // deliver this event (e.g. cancelled from another surface,
            // so no local fetch-abort). Resolve the partial turn: keep
            // the streamed text, append the stopped marker, and clear
            // any perpetual tool spinners. The proxy has already
            // persisted this partial turn server-side.
            markStopped();
            break;
          }
          case 'error': {
            const { code, message } = data as ErrorEvent;
            const isBudgetError = code === 'ai_disabled' || code === 'budget_exceeded' || code === 'credits_exhausted';
            const prefix = isBudgetError ? '🚫' : '⚠️';
            setMessages((prev) =>
              prev.map((m) =>
                m.id === msgId
                  ? {
                      ...m,
                      content: m.content + (m.content ? '\n\n' : '') + `${prefix} ${message}`,
                      errorCode: code,
                    }
                  : m,
              ),
            );
            break;
          }
        }
      };

      try {
        const parser = createSSEParser(handleEvent);
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          parser.push(value);
        }
      } catch (err: any) {
        // An intentional Stop aborts the fetch, which rejects the
        // pending reader.read() with an AbortError. Swallow it so the
        // partial assistant message already rendered into the atom is
        // KEPT (not replaced by an error) — that's what lets auto-save
        // persist it to SaaS chat history. Re-throw anything else so
        // useChatSend's catch can surface a genuine connection failure.
        if (err?.name !== 'AbortError') throw err;
        // The abort usually closes the connection before the server's
        // `message_cancelled` event can arrive, so resolve the turn
        // here too: clear in-flight tool spinners and mark the message
        // stopped. markStopped is idempotent if the event did land.
        markStopped();
      } finally {
        setStreaming(false);
        setTurnId(null);
      }
    },
    [setMessages, setStreaming, setSessionId, setTurnId, applyAiSearchIntent, actionMode, dispatchUIAction],
  );

  return { processStream };
}
